import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from '@/components/ui/Toast'
import { useAuthStore } from '@/store/auth.store'

/** Perfil público de un negocio + su historial de posts. */
export function useBusinessProfile(id: string | undefined) {
  return useQuery({
    queryKey: ['business', id],
    queryFn: () => api.get<{ data: Record<string, unknown> }>(`/api/businesses/${id}`),
    enabled: !!id,
  })
}

/** IDs de los negocios que sigue el usuario logueado. */
export function useMyFollows() {
  const user = useAuthStore((s) => s.user)

  const query = useQuery({
    queryKey: ['follows'],
    queryFn: () => api.get<{ businessIds: string[] }>('/api/businesses/followed'),
    enabled: !!user,
    staleTime: 1000 * 30,
  })

  return new Set(query.data?.businessIds ?? [])
}

export function useToggleFollow(businessId: string) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (following: boolean) =>
      api.post(`/api/businesses/${businessId}/follow`, { following }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['follows'] })
      qc.invalidateQueries({ queryKey: ['following'] })
      qc.invalidateQueries({ queryKey: ['business', businessId] })
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

/** Negocios que sigue el usuario logueado, con datos completos para listarlos. */
export function useFollowedBusinesses() {
  const user = useAuthStore((s) => s.user)

  return useQuery({
    queryKey: ['following'],
    queryFn: () => api.get<{ data: Record<string, unknown>[] }>('/api/businesses/following'),
    enabled: !!user,
    staleTime: 1000 * 30,
  })
}
